import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { AuthPage } from "../../components/auth-page.js";
import { useAuth } from "../../lib/auth-context.js";
import { LoadingState } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";

/**
 * The token handoff. PUBLIC, and not a destination — it reads and redirects.
 *
 * The access token arrives in the URL FRAGMENT, which browsers never send to a server, so it stays
 * out of access logs. It is read once and the fragment cleared immediately, so it does not linger
 * in history either. Nothing is written to storage (NFR-002).
 */
export const Route = createFileRoute("/auth/landing")({
  component: AuthLanding,
});

/**
 * Captured at module evaluation, not inside the effect.
 *
 * The effect must clear the fragment as soon as it has read it, which makes it DESTRUCTIVE — and
 * React StrictMode invokes effects twice in development. The first run consumed the token and
 * cleared the fragment; the second found nothing and concluded the person was not signed in, so
 * every development sign-in bounced back to /sign-in. Capturing at import time happens exactly once
 * per page load, before any effect can run.
 */
const initialAuthFragment =
  typeof window === "undefined" ? "" : window.location.hash.replace(/^#/, "");

function AuthLanding(): ReactNode {
  const t = useT();
  const { setAccessToken, finishRestoring } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(initialAuthFragment);
    const token = params.get("access_token");
    const next = params.get("next") ?? "/";

    window.history.replaceState(null, "", window.location.pathname);

    if (!token) {
      finishRestoring();
      void navigate({ to: "/sign-in", replace: true });
      return;
    }

    setAccessToken(token);
    finishRestoring();
    void navigate({ to: next, replace: true });
  }, [setAccessToken, finishRestoring, navigate]);

  return (
    <AuthPage>
      <LoadingState>{t("auth.signingIn")}</LoadingState>
    </AuthPage>
  );
}
